import { useEffect, useCallback } from 'react';
import { useWebSocket } from './useWebSocket';
import { Match, Player } from '../types';

interface UseMatchWebSocketProps {
  matchId: string | undefined;
  wsConnected: boolean;
  onMatchUpdate: (match: Match) => void;
  onTeamsUpdate: (teams: { team1: (Player | null)[]; team2: (Player | null)[] }) => void;
  onPlayersSwapped: (match: Match) => void;
  onPlayerAdded: (match: Match) => void;
  onPlayerRemoved: (match: Match) => void;
  onPlayerUpdated: (match: Match) => void;
  onPlayerPromotedDemoted: (match: Match) => void;
  onPlayerMoved: (match: Match) => void;
  onSelfReportValidated: (data: any) => void;
  onSelfReportRejected: (data: any) => void;
  onSelfReportSubmitted: (data: any) => void;
  onValidationRequestCreated: (data: any) => void;
  onSpaceOccupied: (spaceKey: string) => void;
  onSpaceReleased: (spaceKey: string) => void;
  onShowNotification: (message: string, type: 'success' | 'error' | 'info') => void;
  onFetchMatch: () => Promise<void>;
  buildTeamsFromMatch: (match: Match) => { team1: (Player | null)[]; team2: (Player | null)[] };
  // Nuevos callbacks para eventos adicionales
  onMatchFinishedWithScore?: (finalScore: any) => void;
  onStatsReported?: (reportData: any) => void;
  onReviewCreated?: (reviewData: any) => void;
  onLocationUpdated?: (locationData: any) => void;
  onAdminTransferred?: (newAdminData: any) => void;
  onPlayerEdited?: (playerData: any) => void;
  onStatsValidationCompleted?: (validationData: any) => void;
  // Nuevos callbacks para eventos adicionales
  onPlayerReplaced?: (data: any) => void;
  onPlayerRemovedByAdmin?: (data: any) => void;
  onSubstitutePromoted?: (data: any) => void;
  onPlayerSelfRemoved?: (data: any) => void;
  onVoteCast?: (data: any) => void;
  onOwnershipTransferred?: (data: any) => void;
}

export const useMatchWebSocket = ({
  matchId,
  wsConnected,
  onMatchUpdate,
  onTeamsUpdate,
  onPlayersSwapped,
  onPlayerAdded,
  onPlayerRemoved,
  onPlayerUpdated,
  onPlayerPromotedDemoted,
  onPlayerMoved,
  onSelfReportValidated,
  onSelfReportRejected,
  onSelfReportSubmitted,
  onValidationRequestCreated,
  onSpaceOccupied,
  onSpaceReleased,
  onShowNotification,
  onFetchMatch,
  buildTeamsFromMatch,
  // Nuevos callbacks
  onMatchFinishedWithScore,
  onStatsReported,
  onReviewCreated,
  onLocationUpdated,
  onAdminTransferred,
  onPlayerEdited,
  onStatsValidationCompleted,
  // Nuevos callbacks
  onPlayerReplaced,
  onPlayerRemovedByAdmin,
  onSubstitutePromoted,
  onPlayerSelfRemoved,
  onVoteCast,
  onOwnershipTransferred
}: UseMatchWebSocketProps) => {
  const { 
    occupySpace, 
    releaseSpace, 
    isConnected: wsConnectedFromHook,
    joinMatch: joinMatchWS
  } = useWebSocket((data) => {
    if (data.type === 'MATCH_UPDATED' || data.type === 'MATCH_UPDATE') {
      // Server emits `{ data: { match } }`; older callers pass the match flat.
      // Handle both shapes for backwards compatibility.
      const matchObj = data.data?.match || data.data;
      if (!matchObj) return;
      onMatchUpdate(matchObj);
      if (matchObj.teamData) {
        onTeamsUpdate(matchObj.teamData);
      } else if (matchObj.lastTeams || matchObj.players) {
        const teams = buildTeamsFromMatch(matchObj);
        onTeamsUpdate(teams);
      }
    } else if (data.type === 'TEAMS_UPDATED') {
      onTeamsUpdate(data.data);
    } else if (data.type === 'PLAYERS_SWAPPED') {
      // Actualizar el partido completo cuando se intercambian jugadores
      onPlayersSwapped(data.data.match);
      // Reconstruir equipos desde los datos actualizados
      if (data.data.match) {
        const teams = buildTeamsFromMatch(data.data.match);
        onTeamsUpdate(teams);
      }
    } else if (data.type === 'PLAYER_ADDED' || data.type === 'SUBSTITUTE_ADDED') {
      // Actualizar el partido cuando alguien se une
      if (data.data.match) {
        onPlayerAdded(data.data.match);
        // Reconstruir equipos
        const teams = buildTeamsFromMatch(data.data.match);
        onTeamsUpdate(teams);
      }
    } else if (data.type === 'SELF_REPORT_VALIDATED') {
      // Actualizar stats cuando se valida un auto-reporte
      if (data.data && data.data.selfReport) {
        const { playerId, goals, assists, saves, validator } = data.data.selfReport;
        
        onSelfReportValidated({
          playerId,
          goals,
          assists,
          saves,
          validator,
          playerName: data.data.playerName,
          achievements: data.data.achievements
        });
        
        // Mostrar notificación con información de logros si aplica
        let notificationMessage = `✅ Reporte de ${data.data.playerName} validado por ${validator.name}`;
        
        if (data.data.achievements) {
          const achievements = [];
          if (data.data.achievements.isTopScorer) achievements.push('⚽ Goleador');
          if (data.data.achievements.isTopAssister) achievements.push('➡️ Asistidor');
          if (data.data.achievements.isTopSaver) achievements.push('🧤 Guante de Oro');
          
          if (achievements.length > 0) {
            notificationMessage += ` - Logros: ${achievements.join(', ')}`;
          }
        }
        
        onShowNotification(notificationMessage, 'success');
        
        // Recargar datos del partido para actualizar logros en tiempo real
        onFetchMatch();
      }
    } else if (data.type === 'SELF_REPORT_REJECTED') {
      // Actualizar stats cuando se rechaza un auto-reporte
      if (data.data && data.data.selfReport) {
        const { playerId, rejectionReason, rejectedBy } = data.data.selfReport;
        
        onSelfReportRejected({
          playerId,
          rejectionReason,
          rejectedBy,
          playerName: data.data.playerName
        });
        
        onShowNotification(`❌ Reporte de ${data.data.playerName} rechazado por ${rejectedBy}`, 'error');
      }
    } else if (data.type === 'SELF_REPORT_SUBMITTED') {
      // Actualizar cuando se envía un nuevo auto-reporte
      if (data.data && data.data.selfReport) {
        const { playerId, goals, assists, saves } = data.data.selfReport;
        
        onSelfReportSubmitted({
          playerId,
          goals,
          assists,
          saves,
          playerName: data.data.playerName
        });
        
        onShowNotification(`📝 ${data.data.playerName} envió su reporte`, 'info');
      }
    } else if (data.type === 'VALIDATION_REQUEST_CREATED') {
      // Actualizar cuando se crea una nueva solicitud de validación
      if (data.data && data.data.validationRequest) {
        onValidationRequestCreated({
          playerName: data.data.playerName
        });
        
        onShowNotification(`🔍 Nueva solicitud de validación para ${data.data.playerName}`, 'info');
      }
    } else if (data.type === 'PLAYER_REMOVED' || data.type === 'SUBSTITUTE_REMOVED') {
      // Actualizar el partido cuando alguien se va
      if (data.data.match) {
        onPlayerRemoved(data.data.match);
        // Reconstruir equipos
        const teams = buildTeamsFromMatch(data.data.match);
        onTeamsUpdate(teams);
      }
    } else if (data.type === 'PLAYER_UPDATED') {
      // Actualizar el partido cuando se actualiza un jugador
      if (data.data.match) {
        onPlayerUpdated(data.data.match);
      }
    } else if (data.type === 'player_promoted_demoted') {
      // Actualizar el partido cuando un jugador es promovido/descendido
      if (data.data && data.data.match) {
        onPlayerPromotedDemoted(data.data.match);
        // Reconstruir equipos
        const teams = buildTeamsFromMatch(data.data.match);
        onTeamsUpdate(teams);
      }
    } else if (data.type === 'PLAYER_MOVED') {
      // Actualizar el partido cuando un jugador es movido a una posición específica
      if (data.data && data.data.match) {
        onPlayerMoved(data.data.match);
        // Reconstruir equipos de forma robusta
        const teams = buildTeamsFromMatch(data.data.match);
        onTeamsUpdate(teams);
      }
    } else if (data.type === 'SPACE_OCCUPIED') {
      onSpaceOccupied(data.data.spaceKey);
    } else if (data.type === 'SPACE_RELEASED') {
      onSpaceReleased(data.data.spaceKey);
    } else if (data.type === 'MATCH_FINISHED_WITH_SCORE') {
      // Manejar partido finalizado con resultado
      onMatchFinishedWithScore?.(data.data.finalScore);
      onShowNotification('🏁 Partido finalizado con resultado', 'success');
    } else if (data.type === 'STATS_REPORTED') {
      // Manejar reporte de estadísticas
      onStatsReported?.(data.data.reportData);
      onShowNotification('📊 Estadísticas reportadas', 'info');
    } else if (data.type === 'REVIEW_CREATED') {
      // Manejar review creada
      onReviewCreated?.(data.data.reviewData);
      onShowNotification('⭐ Nueva review creada', 'info');
    } else if (data.type === 'LOCATION_UPDATED') {
      // Manejar ubicación actualizada
      onLocationUpdated?.(data.data.locationData);
      onShowNotification('📍 Ubicación actualizada', 'info');
    } else if (data.type === 'ADMIN_TRANSFERRED') {
      // Manejar administración transferida
      onAdminTransferred?.(data.data.newAdminData);
      onShowNotification('👑 Administración transferida', 'info');
    } else if (data.type === 'PLAYER_EDITED') {
      // Manejar jugador editado
      onPlayerEdited?.(data.data.playerData);
      onShowNotification('✏️ Jugador editado', 'info');
    } else if (data.type === 'STATS_VALIDATION_COMPLETED') {
      // Manejar validación de stats completada
      onStatsValidationCompleted?.(data.data.validationData);
      onShowNotification('✅ Validación de estadísticas completada', 'success');
    } else if (data.type === 'PLAYER_REPLACED') {
      // Manejar jugador reemplazado
      onPlayerReplaced?.(data.data);
      onShowNotification(`🔄 ${data.data.newPlayer.name} reemplazó a un jugador invitado`, 'info');
      // Actualizar el partido completo
      if (data.data.match) {
        onMatchUpdate(data.data.match);
        const teams = buildTeamsFromMatch(data.data.match);
        onTeamsUpdate(teams);
      }
    } else if (data.type === 'PLAYER_REMOVED_BY_ADMIN') {
      // Manejar jugador removido por admin
      onPlayerRemovedByAdmin?.(data.data);
      onShowNotification(`❌ ${data.data.removedPlayer.name} fue eliminado por el admin`, 'info');
      // Actualizar el partido completo
      if (data.data.match) {
        onMatchUpdate(data.data.match);
        const teams = buildTeamsFromMatch(data.data.match);
        onTeamsUpdate(teams);
      }
    } else if (data.type === 'SUBSTITUTE_PROMOTED') {
      // Manejar suplente promovido
      onSubstitutePromoted?.(data.data);
      onShowNotification(`⬆️ ${data.data.promotedPlayer.name} fue promovido a jugador principal`, 'success');
      // Actualizar el partido completo
      if (data.data.match) {
        onMatchUpdate(data.data.match);
        const teams = buildTeamsFromMatch(data.data.match);
        onTeamsUpdate(teams);
      }
    } else if (data.type === 'PLAYER_SELF_REMOVED' || data.type === 'SUBSTITUTE_SELF_REMOVED') {
      // Manejar auto-remoción de jugador
      onPlayerSelfRemoved?.(data.data);
      onShowNotification(`👋 ${data.data.removedPlayer.name} se retiró del partido`, 'info');
      // Actualizar el partido completo
      if (data.data.match) {
        onMatchUpdate(data.data.match);
        const teams = buildTeamsFromMatch(data.data.match);
        onTeamsUpdate(teams);
      }
    } else if (data.type === 'VOTE_CAST') {
      // Manejar voto emitido
      onVoteCast?.(data.data);
      onShowNotification(`🗳️ ${data.data.votedPlayerName} recibió un voto`, 'info');
    } else if (data.type === 'OWNERSHIP_TRANSFERRED') {
      // Manejar transferencia de propiedad
      onOwnershipTransferred?.(data.data);
      onShowNotification(`👑 ${data.data.newOwnerName} es ahora el nuevo organizador`, 'success');
      // Actualizar el partido completo
      if (data.data.match) {
        onMatchUpdate(data.data.match);
      }
    }
  }, (error) => {
    // eslint-disable-next-line no-console
    console.error('Error WebSocket en useMatchWebSocket:', error);
  });

  // Join WebSocket room when connected
  useEffect(() => {
    if (wsConnected && matchId) {
      joinMatchWS(matchId);
    }
  }, [wsConnected, matchId, joinMatchWS]);

  return {
    occupySpace,
    releaseSpace,
    isConnected: wsConnectedFromHook,
    joinMatch: joinMatchWS
  };
};
